import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Slot, Fair, Reservation, ReservationCreate, ReservationDetail,
  ReservationListResponse, SlotAvailabilityCheck, ReservationFlowState
} from '../types';
import { reservationApi, rentalApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

interface ReservationContextType {
  // Flow state for wizard
  flow: ReservationFlowState;
  updateFlow: (data: Partial<ReservationFlowState>) => void;
  resetFlow: () => void;

  // Reservations list
  reservations: Reservation[];
  currentReservation: ReservationDetail | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  initializeFlow: (slot: Slot, fair: Fair, vendorId: number) => void;
  calculatePrice: () => number;
  checkAvailability: (slotId: number, reservationType: string, dates?: string[]) => Promise<SlotAvailabilityCheck>;
  submitReservation: () => Promise<Reservation>;
  loadMyReservations: (status?: string, page?: number) => Promise<void>;
  loadReservationDetails: (reservationId: number) => Promise<void>;
  cancelReservation: (reservationId: number, reason?: string) => Promise<void>;
  clearError: () => void;
}

const initialFlowState: ReservationFlowState = {
  slot: null,
  fair: null,
  vendor_id: null,
  reservation_type: null,
  selected_dates: [],
  rental_items: [],
  special_requests: '',
  discount_code: null, // Issue #375
  discount_valid: false, // Issue #375
  discount_type: null, // Issue #375
  discount_value: null, // Issue #375
  original_price: 0, // Issue #375
  total_price: 0,
  step: 1,
};

const ReservationContext = createContext<ReservationContextType | null>(null);

interface ReservationProviderProps {
  children: ReactNode;
}

export function ReservationProvider({ children }: ReservationProviderProps) {
  const [flow, setFlow] = useState<ReservationFlowState>(initialFlowState);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [currentReservation, setCurrentReservation] = useState<ReservationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const updateFlow = useCallback((data: Partial<ReservationFlowState>) => {
    setFlow(prev => ({ ...prev, ...data }));
  }, []);

  const resetFlow = useCallback(() => {
    setFlow(initialFlowState);
  }, []);

  const initializeFlow = useCallback((slot: Slot, fair: Fair, vendorId: number) => {
    setFlow({
      ...initialFlowState,
      slot,
      fair,
      vendor_id: vendorId,
      step: 1,
    });
  }, []);

  const calculatePrice = useCallback((): number => {
    if (!flow.slot || !flow.fair) return 0;

    const basePrice = flow.slot.base_price;
    let originalPrice = 0;

    if (flow.reservation_type === 'full_duration') {
      if (flow.fair.start_date && flow.fair.end_date) {
        // Parse dates as UTC to avoid timezone/DST issues
        const [startYear, startMonth, startDay] = flow.fair.start_date.split('-').map(Number);
        const [endYear, endMonth, endDay] = flow.fair.end_date.split('-').map(Number);
        const startDate = Date.UTC(startYear, startMonth - 1, startDay);
        const endDate = Date.UTC(endYear, endMonth - 1, endDay);
        const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        originalPrice = basePrice * days;
      } else {
        originalPrice = basePrice;
      }
    } else if (flow.reservation_type === 'per_day') {
      originalPrice = basePrice * flow.selected_dates.length;
    }

    // Apply discount if valid (Issue #375)
    if (flow.discount_valid && flow.discount_type && flow.discount_value !== null) {
      if (flow.discount_type === 'percentage') {
        const discountAmount = (originalPrice * flow.discount_value) / 100;
        return Math.max(0, originalPrice - discountAmount);
      } else if (flow.discount_type === 'fixed_amount') {
        return Math.max(0, originalPrice - flow.discount_value);
      } else if (flow.discount_type === 'free') {
        return 0;
      }
    }

    return originalPrice;
  }, [flow.slot, flow.fair, flow.reservation_type, flow.selected_dates, flow.discount_valid, flow.discount_type, flow.discount_value]);

  const checkAvailability = useCallback(async (
    slotId: number,
    reservationType: string,
    dates?: string[]
  ): Promise<SlotAvailabilityCheck> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await reservationApi.checkAvailability(slotId, reservationType, dates);
      return result;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const submitReservation = useCallback(async (): Promise<Reservation> => {
    if (!flow.slot || !flow.vendor_id || !flow.reservation_type) {
      throw new Error('Missing required reservation data');
    }

    try {
      setIsLoading(true);
      setError(null);

      const data: ReservationCreate = {
        vendor_id: flow.vendor_id,
        slot_id: flow.slot.id,
        reservation_type: flow.reservation_type,
        selected_dates: flow.reservation_type === 'per_day' ? flow.selected_dates : undefined,
        notes: flow.special_requests || undefined,
        discount_code: flow.discount_valid && flow.discount_code ? flow.discount_code : undefined, // Issue #375
      };

      const reservation = await reservationApi.createReservation(data);

      // Add rental items to the reservation if any were selected
      if (flow.rental_items && flow.rental_items.length > 0) {
        for (const rentalItem of flow.rental_items) {
          await rentalApi.addRentalToReservation(reservation.id, rentalItem);
        }
      }

      return reservation;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [flow]);

  const loadMyReservations = useCallback(async (status?: string, page: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      const response: ReservationListResponse = await reservationApi.getMyReservations(status, page);
      setReservations(response.items);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load reservations:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadReservationDetails = useCallback(async (reservationId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const details = await reservationApi.getReservationDetails(reservationId);
      setCurrentReservation(details);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Failed to load reservation details:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const cancelReservation = useCallback(async (reservationId: number, reason?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await reservationApi.cancelReservation(reservationId, reason);
      // Update local state
      setReservations(prev =>
        prev.map(r => r.id === reservationId ? { ...r, status: 'cancelled' as const } : r)
      );
      if (currentReservation?.id === reservationId) {
        setCurrentReservation(prev => prev ? { ...prev, status: 'cancelled' } : null);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      throw new Error(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentReservation]);

  const value: ReservationContextType = {
    flow,
    updateFlow,
    resetFlow,
    reservations,
    currentReservation,
    isLoading,
    error,
    initializeFlow,
    calculatePrice,
    checkAvailability,
    submitReservation,
    loadMyReservations,
    loadReservationDetails,
    cancelReservation,
    clearError,
  };

  return (
    <ReservationContext.Provider value={value}>
      {children}
    </ReservationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReservation(): ReservationContextType {
  const context = useContext(ReservationContext);
  if (!context) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
}

export default ReservationContext;
